import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Upload, Sparkles, CheckCircle2, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Preset lineup: Summer / Fall 2026 (5/24 → 9/6). Each line is `YYYY-MM-DD | passage/title`.
const SUMMER_2026_PRESET = `2026-05-24 | Matthew 10 — Pentecost: Jesus Sends Them Out in Power
2026-05-31 | Matthew 17:1-13 — Jesus' Transfiguration
2026-06-07 | Matthew 17:14-20 — Jesus Drives Out a Demon the Disciples Could Not
2026-06-14 | Matthew 14:22-36 — Jesus Walks on Water
2026-06-21 | Matthew 20:20-28 — A Mother's Request
2026-06-28 | Matthew 22:15-22 — Jesus Tested by Humans: Jesus and the Third Way
2026-07-05 | Matthew 26:6-13 — Jesus Anointed at Bethany
2026-07-12 | God the Father — Suggested texts: John 14:1-14; Matthew 6:9-13; 1 John 3:1
2026-07-19 | God the Son — Suggested texts: John 1:1-18; Philippians 2:5-11; Colossians 1:15-20
2026-07-26 | God the Spirit — Suggested texts: John 14:15-27; Acts 2:1-21; Galatians 5:16-25
2026-08-02 | The Holy Catholic Church, Communion of Saints, Resurrection of the Dead — Suggested texts: Ephesians 4:1-16; 1 Corinthians 15:12-28; Hebrews 12:1-2
2026-08-09 | Life-Giving Holiness — Suggested texts: Romans 12:1-2; 1 Peter 1:13-25; Leviticus 19:1-2, 9-18
2026-08-16 | Love-Driven Justice — Suggested texts: Micah 6:6-8; James 2:1-17; Isaiah 58:6-12
2026-08-23 | Christ-Compelled Multiplication — Suggested texts: Matthew 28:16-20; 2 Timothy 2:1-2; Acts 1:6-8
2026-08-30 | Cross-Cultural Collaboration — Suggested texts: Acts 10:1-48; Galatians 3:26-29; Revelation 7:9-12
2026-09-06 | God-Given Revelation — Suggested texts: 2 Timothy 3:14-17; Hebrews 1:1-3; Psalm 19`;

// Accept the pipe `|` separator (preferred) and tolerate `,` or ` - ` as fallback.
function parseLines(text) {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('#'))
    .map((line) => {
      // Try splitting on '|' first.
      let sep = line.indexOf('|');
      if (sep === -1) {
        // Fall back: first whitespace after the date (YYYY-MM-DD is 10 chars).
        const dateMatch = line.match(/^(\d{4}-\d{2}-\d{2})\s+(.+)$/);
        if (dateMatch) return { week_start_date: dateMatch[1], passage: dateMatch[2].trim() };
        return { week_start_date: line, passage: '' };
      }
      return {
        week_start_date: line.slice(0, sep).trim(),
        passage: line.slice(sep + 1).trim(),
      };
    });
}

const statusBadge = (status) => {
  if (status === 'added') return <Badge className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Added</Badge>;
  if (status === 'replaced') return <Badge className="bg-blue-600 hover:bg-blue-700"><Sparkles className="w-3 h-3 mr-1" />Replaced</Badge>;
  if (status === 'skipped') return <Badge variant="outline" className="text-amber-700 border-amber-400"><AlertCircle className="w-3 h-3 mr-1" />Skipped</Badge>;
  return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
};

export const BulkSermonImport = ({ onComplete }) => {
  const { API } = useContext(AuthContext);
  const [text, setText] = useState('');
  const [overwrite, setOverwrite] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const parsed = parseLines(text);
  const validCount = parsed.filter((p) => p.week_start_date && p.passage && /^\d{4}-\d{2}-\d{2}$/.test(p.week_start_date)).length;
  const invalidCount = parsed.length - validCount;

  const handleLoadPreset = () => {
    setText(SUMMER_2026_PRESET);
    setResults(null);
  };

  const handleSubmit = async () => {
    const items = parsed.filter((p) => p.week_start_date && p.passage);
    if (items.length === 0) {
      toast.error('Nothing valid to import — check the format');
      return;
    }
    setSubmitting(true);
    setResults(null);
    try {
      const response = await axios.post(`${API}/admin/sermons/bulk-import`, {
        items,
        overwrite,
      }, { timeout: 600000 }); // up to 10 min for large batches
      setResults(response.data);
      const { added_or_replaced, skipped, failed } = response.data;
      toast.success(`Imported ${added_or_replaced} sermon${added_or_replaced === 1 ? '' : 's'}` +
        (skipped ? ` • ${skipped} skipped` : '') +
        (failed ? ` • ${failed} failed` : ''));
      if (onComplete) onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk import failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3" data-testid="bulk-sermon-import">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-semibold text-blue-900 flex items-center gap-2">
            <Upload className="w-4 h-4" /> Bulk Import Sermons
          </h4>
          <p className="text-xs text-blue-700 mt-1">
            One sermon per line in the format: <code className="bg-white px-1 py-0.5 rounded border">YYYY-MM-DD | passage or title</code>.
            AI will auto-generate study + parent questions for each entry.
          </p>
        </div>
        <Button
          onClick={handleLoadPreset}
          variant="outline"
          size="sm"
          className="border-blue-500 text-blue-700 hover:bg-blue-100"
          data-testid="load-summer-2026-preset"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Load Summer/Fall 2026 lineup
        </Button>
      </div>

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`2026-05-24 | Matthew 10 — Pentecost: Jesus Sends Them Out in Power\n2026-05-31 | Matthew 17:1-13 — Jesus' Transfiguration\n...`}
        rows={10}
        className="bg-white font-mono text-xs"
        data-testid="bulk-import-textarea"
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-600 space-y-1">
          <p>
            <span className="font-medium text-green-700">{validCount}</span> valid •{' '}
            {invalidCount > 0 && (
              <span className="text-red-600">{invalidCount} invalid</span>
            )}
            {invalidCount === 0 && <span className="text-slate-500">0 invalid</span>}
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              className="accent-blue-600"
              data-testid="bulk-overwrite-checkbox"
            />
            <span>Overwrite existing sermons on these dates</span>
          </label>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={submitting || validCount === 0}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="bulk-import-submit"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing & generating questions…</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> Import {validCount} sermon{validCount === 1 ? '' : 's'}</>
          )}
        </Button>
      </div>

      {submitting && (
        <p className="text-xs text-slate-600 italic">
          This can take ~3–5 seconds per sermon while AI generates questions. Please wait…
        </p>
      )}

      {results && (
        <div className="mt-3 bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="bulk-import-results">
          <div className="px-3 py-2 border-b bg-slate-50 text-sm font-medium text-slate-700">
            Results: {results.added_or_replaced} added/replaced • {results.skipped} skipped • {results.failed} failed
          </div>
          <div className="max-h-64 overflow-y-auto divide-y">
            {results.results.map((r, i) => (
              <div key={i} className="px-3 py-2 flex items-start gap-3 text-sm">
                <div className="flex-shrink-0 w-24 text-xs text-slate-500 font-mono pt-0.5">{r.week_start_date}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-800 truncate">{r.passage || <span className="italic text-slate-400">(empty)</span>}</p>
                  {r.error && <p className="text-xs text-red-600 mt-0.5">{r.error}</p>}
                  {r.study_count != null && (
                    <p className="text-xs text-slate-500 mt-0.5">{r.study_count} study • {r.parent_count} parent questions</p>
                  )}
                </div>
                <div className="flex-shrink-0">{statusBadge(r.status)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkSermonImport;
